import { defineSprite, buildSilhouette, outlineRows, smoothUpscale, darkenHex } from '../core/Sprite.js';
import { WEAPONS } from '../../config/constants.js';

// How many extra grid cells buildSilhouette gets per original "radius unit"
// for the player + minion blobs (the ones close enough on-screen during
// combat that their low native cell count reads as chunky/blocky). Rendered
// at a proportionally smaller pixelSize so the actual on-screen footprint is
// unchanged — this only makes each blob's outline/spikes rounder and its
// accent (facing dot, fuse spark) finer-grained, not bigger or smaller.
// Raise for even smoother silhouettes; each +1 roughly quadruples the
// fillRect calls Sprite.js issues per draw, so there's a real perf cost to
// pushing this much higher.
const DETAIL = 2;

// buildSilhouette + outlineRows always adds a fixed 1-cell border regardless
// of radius, so naively passing `radius * DETAIL` and dividing pixelSize by
// DETAIL would shrink the *rendered* footprint slightly — that fixed border
// shrinks to 1/DETAIL of a buffer pixel instead of staying a full one. This
// compensates so the finished silhouette's footprint matches what
// buildSilhouette(radius) + a pixelSize:1 border would have rendered.
function detailedRadius(radius, detail) {
  return radius * detail + (detail - 1);
}

// Shared finishing pass for every small creature sprite built from a
// buildSilhouette blob (player characters, minions, pets) — bottom-edge
// shading for volume plus a dark outline, the same two Sprite.js
// primitives (darkenHex/outlineRows) pets already use (see PET_SPRITES
// below). Any accent pixels (a player's facing dot, a bomber's fuse spark)
// should already be placed in `rows` before this runs — shadeBelow only
// touches 'R' cells so they're left alone, and outlineRows treats them as
// filled either way. `extraPalette` merges in colors for those accent keys.
function finishCreature(rows, bodyColor, extraPalette = {}) {
  const shaded = shadeBelow(rows, 0.6, 'S');
  const outlined = outlineRows(shaded, 'O');
  const palette = { R: bodyColor, S: darkenHex(bodyColor, 0.35), O: '#0a0a12', ...extraPalette };
  return { rows: outlined, palette };
}

// Fills a `size x size` block of `char` centered on (row, col) — scales a
// single-cell accent (facing dot, fuse spark, casting core) up alongside a
// higher-DETAIL silhouette so it stays as visually readable as it was at the
// original grid resolution instead of shrinking to a barely-visible speck.
function stampAccent(rows, row, col, size, char) {
  const grid = rows.map((r) => r.split(''));
  const half = Math.floor(size / 2);
  for (let dy = -half; dy < size - half; dy++) {
    for (let dx = -half; dx < size - half; dx++) {
      if (grid[row + dy]?.[col + dx] !== undefined) grid[row + dy][col + dx] = char;
    }
  }
  return grid.map((r) => r.join(''));
}

// Player silhouettes — small humanoid-ish blobs, one palette per character
// so each reads as a distinct color at a glance in the middle of a bullet
// storm. 'R' is the body (overridden to white for the dash i-frame flash,
// see Game.js's _drawPlayer), 'K' is a small "facing" accent dot.
function buildPlayerSprite(bodyColor, accentColor) {
  const rows = buildSilhouette(detailedRadius(4, DETAIL), { seed: 7 });
  const centerRow = Math.floor(rows.length / 2);
  const centerCol = Math.floor(rows[0].length / 2);
  // Original placed the dot 2 cells right of center out of a radius-4 grid —
  // same 0.5-of-radius offset, scaled to whatever the new grid's half-width is.
  const withAccent = stampAccent(rows, centerRow, centerCol + Math.round(centerCol * 0.5), DETAIL, 'K');
  const { rows: finished, palette } = finishCreature(withAccent, bodyColor, { K: accentColor });
  return defineSprite({ pixelSize: 1 / DETAIL, palette, rows: finished });
}

export const PLAYER_SPRITES = {
  vess: buildPlayerSprite('#5ad1e6', '#0a0a12'),
  rook: buildPlayerSprite('#7ad67a', '#0a0a12'),
  iris: buildPlayerSprite('#e07ae0', '#0a0a12'),
  thorn: buildPlayerSprite('#e0955a', '#0a0a12'),
  // Matches Nova Pulse's own color (config/constants.js WEAPONS.novaPulse) —
  // ties Kestrel's identity to her signature weapon, and is clearly distinct
  // from every other character's hue.
  kestrel: buildPlayerSprite('#ffd166', '#0a0a12'),
  // Act II champions. Aria matches the Resonor's violet (WEAPONS.resonor);
  // Cairn is the roster's one grey — stone, and the only champion whose color
  // isn't a light source, which is the whole idea of the character.
  aria: buildPlayerSprite('#c9a0ff', '#0a0a12'),
  cairn: buildPlayerSprite('#8a9bb0', '#0a0a12'),
  // Act III champions. Quill takes the Ostinato's gold pulled toward ink
  // (Kestrel already owns that exact gold — see data/characters.js); Fugue
  // takes the Manifold's blue, for two of a thing where there should be one.
  quill: buildPlayerSprite('#e0a63c', '#0a0a12'),
  fugue: buildPlayerSprite('#66d9ff', '#0a0a12'),
  // The polarity champion (BULLET-HELL.md §D7). Rose: the roster's one hue
  // between its warm and cold families, which is the character — see
  // data/characters.js for why it is not the polarity violet.
  tine: buildPlayerSprite('#ff6fae', '#0a0a12'),
};

// Recolors any already-filled ('R') pixel within rows [startRow, endRow) to
// `accentChar` — a colored "crest" band that follows whatever the
// silhouette's jagged edge actually is there, rather than a flat
// single-tone fill across the whole body. Takes an explicit row range
// (rather than a fraction of the whole grid) so callers can target the
// actual hood/collar region of a piece even after it's been merged onto a
// larger padded canvas alongside other pieces.
function capBandRange(rows, startRow, endRow, accentChar) {
  const grid = rows.map((r) => r.split(''));
  const from = Math.max(0, Math.round(startRow));
  const to = Math.min(grid.length, Math.round(endRow));
  for (let y = from; y < to; y++) {
    for (let x = 0; x < grid[y].length; x++) {
      if (grid[y][x] === 'R') grid[y][x] = accentChar;
    }
  }
  return grid.map((r) => r.join(''));
}

// Forces two symmetric pixels at an explicit row to `accentChar` — a
// glowing eye pair positioned to land on the "face" of a silhouette. Takes
// an absolute row (rather than a fraction of the whole grid) for the same
// reason as capBandRange above: after merging with other pieces, "16% down
// the merged canvas" no longer means "16% down this particular piece".
function addEyesAtRow(rows, accentChar, row, spacing = 2) {
  const grid = rows.map((r) => r.split(''));
  const r = Math.max(0, Math.min(grid.length - 1, Math.round(row)));
  const centerCol = Math.floor(grid[0].length / 2);
  if (grid[r][centerCol - spacing] !== undefined) grid[r][centerCol - spacing] = accentChar;
  if (grid[r][centerCol + spacing] !== undefined) grid[r][centerCol + spacing] = accentChar;
  return grid.map((row) => row.join(''));
}

// Overlays several silhouette grids (each with its own top-left offset) onto
// one shared canvas — lets a boss mask be built from more than one
// buildSilhouette blob instead of a single centered one.
function mergeSilhouettes(size, pieces) {
  const grid = Array.from({ length: size }, () => Array(size).fill(' '));
  for (const { rows, offsetX, offsetY } of pieces) {
    for (let y = 0; y < rows.length; y++) {
      for (let x = 0; x < rows[y].length; x++) {
        const ch = rows[y][x];
        if (ch === ' ') continue;
        const gy = y + offsetY;
        const gx = x + offsetX;
        if (gy >= 0 && gy < size && gx >= 0 && gx < size) grid[gy][gx] = ch;
      }
    }
  }
  return grid.map((row) => row.join(''));
}

// Hand-placed, not procedural like buildSilhouette — there's no formula to
// regenerate these at a different resolution, so getting more detail out of
// them means reshaping the existing art (smoothUpscale) rather than
// re-authoring it. 5x is a lot more forgiving of jagged diagonal edges than
// the original ~17x19 grid; each +1 roughly quadruples the fillRect calls
// Sprite.js issues per draw, but there's only ever one boss on screen at a
// time so that's cheap here even where it wasn't for 20+ minions at once.
const BOSS_DETAIL = 5;

// Bosses deliberately use different silhouettes. At this scale, a strong
// outline and one memorable focal feature read better than shared detail:
// Sovereign = hollow armor, Choir = separated fragments, Throne = broad
// seated mass, King = broken asymmetric body.
function bossSprite(rows, palette) {
  const upscaled = smoothUpscale(rows, BOSS_DETAIL);
  return defineSprite({
    pixelSize: 2 / BOSS_DETAIL,
    palette: { O: '#0a0a12', S: '#36131b', ...palette },
    rows: outlineRows(upscaled, 'O'),
  });
}

export const BOSS_SPRITES = {
  // Gauntlet idols, redesigned (2026-07): each silhouette now leads with
  // the boss's gameplay identity — the Sovereign's hollowed chest (a court
  // with no king inside), the Choir literally riven down its middle, the
  // Throne's branching ember fissures, the King half-deleted into drifting
  // static. Body stays on `R` (the shared crimson damage ramp owns it in
  // game — see Game._drawBoss); the accent letters carry the identity.

  // A crowned regal shell — three-point crown, void mouth, and a hollowed
  // chest cavity with one stolen gold mote floating where a heart should be.
  hollowSovereign: bossSprite([
    '  A    A    A  ', '   AAAAAAAAA   ', '    AAAAAAA    ', '    RRRRRRR    ',
    '   RRWWRWWRR   ', '   RRRRRRRRR   ', '    RRCCCRR    ', '   RRRRRRRRR   ',
    '  RRRCCCCCRRR  ', '  RRCC A CCRR  ', '  RRCCCCCCCRR  ', '  RRRCCCCCRRR  ',
    '   RRRRRRRRR   ', '   RRRRRRRRR   ', '  RRSSRRRSSRR  ', '  RSS RRR SSR  ',
    '  SS   S   SS  ',
  ], { R: '#c0303a', A: '#ffd166', W: '#ffffff', C: '#160b12' }),

  // One figure riven clean down the middle — two half-faces singing at
  // each other across the gap, shard-voices drifting around them.
  rivenChoir: bossSprite([
    '  A         A  ', '      A A      ', '    RRR RRR    ', '   RRRR RRRR   ',
    '   RRWR RWRR   ', '   RRRR RRRR   ', ' A RRCR RCRR A ', '   RRRR RRRR   ',
    '  RRRRR RRRRR  ', '  RRRRR RRRRR  ', ' A RRRR RRRR A ', '   RRRR RRRR   ',
    '    RRR RRR    ', '    SSS SSS    ', '   SSSS SSSS   ', '  A         A  ',
    '     A   A     ',
  ], { R: '#8a1a3a', A: '#c084fc', W: '#ffffff', C: '#2b0a33' }),

  // A molten colossus fused into its seat — ember fissures branch downward
  // through the whole mass toward a slatted throne base.
  ashenThrone: bossSprite([
    '       AA       ', '      ARRA      ', '     RRWWRR     ', '     RRRRRR     ',
    '    RRRAARRR    ', '    RRARRARR    ', '   RRARRRRARR   ', '   RRRRAARRRR   ',
    '  RRRARRRRARRR  ', '  RRARRRRRRARR  ', ' RRRRRAARRRRRR  ', ' RRRARRRRRRARR  ',
    ' RRRRRRRRRRRRRR ', 'RRSSRRRSSRRRSSRR', 'RSSSSSSSSSSSSSSR', 'SSSSSSSSSSSSSSSS',
    'SS   SS    SS SS',
  ], { R: '#b5502a', A: '#ffd166', W: '#fff2b8', C: '#301115' }),

  // Half a king: the left side still regal — crown, eye, robe — the right
  // side already deleted, trailing off into drifting static.
  unmadeKing: bossSprite([
    '  A A      A    ', '  AAAA          ', '  AAAA   A      ', '  RRRR      R   ',
    '  RWRR   R      ', '  RRRR R    R   ', '  RCRR    R     ', '  RRRRRR   R    ',
    '  RRRRR  R    A ', '  RRRRRR    R   ', '  RRRRR  R      ', '  RRRR R    R   ',
    '  RRRRR   R     ', '  RRSR  S    A  ', '  RSSS    S     ', '  SSS  S    S   ',
    '  SS  S    S    ',
  ], { R: '#4a0f18', A: '#ffd166', W: '#ffffff', C: '#120910' }),

  // The true form: barely a column of king left, scattering from BOTH
  // edges now — accents gone hot pink, the crown dissolving too.
  unmadeKingPhase2: bossSprite([
    ' A   AA    A    ', '    AAAA        ', ' A  AAAA  A     ', '    RRRR     R  ',
    '  R RWRR  R     ', '    RRRR    R   ', ' R  RCRR  R     ', '    RRRR     R  ',
    '  R RRRR  R     ', '    RRRR    A   ', ' R  RRR  R      ', '    RRR R   R   ',
    '  R RRR   R     ', '    RSR  S   A  ', ' S  SSS   S     ', '    SS  S   S   ',
    '  S S  S    S   ',
  ], { R: '#7a1020', A: '#ff5d6c', W: '#ffffff', C: '#ffd166' }),

  // --- Gauntlet interlopers (2026-07 expansion, data/bosses.js) ---
  // Same rule as everywhere: the body (R) rides the shared crimson→black
  // damage ramp; identity lives in the accent letters and the silhouette.

  // A metronome obelisk — trapezoid tower, empty pendulum slot down the
  // face (the swinging pendulum itself is drawn live in Game.js's
  // _drawBossSignature so it can tick in hard steps), tick marks flanking
  // the slot like a ruler, steel cap.
  ironCadence: bossSprite([
    '      AAA      ', '     RRRRR     ', '     RRCRR     ', '    RRWCWRR    ',
    '    RRRCRRR    ', '   RRWRCRWRR   ', '   RRRRCRRRR   ', '  RRWRRCRRWRR  ',
    '  RRRRRCRRRRR  ', ' RRWRRRCRRRWRR ', ' RRRRRRCRRRRRR ', ' RRRRRRCRRRRRR ',
    'RRRRRRRCRRRRRRR', 'RRRRRRRRRRRRRRR', 'RSSSSSSSSSSSSSR', 'SSSSSSSSSSSSSSS',
    'SS           SS',
  ], { R: '#3a4258', A: '#9fb8e8', W: '#e8f0ff', C: '#0c0f16' }),

  // A bloated court rolled into one body — tiny greedy eyes above a maw
  // that takes up half the sprite, ringed with teeth, one gold gleam of
  // swallowed treasure deep inside.
  famishedCourt: bossSprite([
    '     RRRRR     ', '   RRRRRRRRR   ', '  RRRWRRRWRRR  ', ' RRRRRRRRRRRRR ',
    ' RRCCCCCCCCCRR ', 'RRCWCWCWCWCWCRR', 'RRCCCCCCCCCCCRR', 'RRCCCCCACCCCCRR',
    'RRCCCCCCCCCCCRR', 'RRWCWCWCWCWCWRR', ' RRRRRRRRRRRRR ', ' RRRRRRRRRRRRR ',
    '  RRRRRRRRRRR  ', '  RSSRRRRRSSR  ', '  SSS RRR SSS  ', '   SS SSS SS   ',
    '    S  S  S    ',
  ], { R: '#4a4420', A: '#ffd166', W: '#e8ecb0', C: '#12100a' }),

  // A veiled consort — three-point crown, pale veil with dark eye slits,
  // and a rose heart worn openly at the chest (the same heart that beats
  // lub-dub in _drawBossSignature), gown flaring to the floor.
  jealousRegent: bossSprite([
    '   A   A   A   ', '   AAAAAAAAA   ', '    RRRRRRR    ', '    RWWWWWR    ',
    '   RRWCWCWRR   ', '   RRWWWWWRR   ', '    RRRRRRR    ', '   RRRRARRRR   ',
    '  RRRRAAARRRR  ', '  RRRRRARRRRR  ', '  RRRRRRRRRRR  ', '   RRRRRRRRR   ',
    '   RRRRRRRRR   ', '  RRRRRRRRRRR  ', '  RSSRRRRRSSR  ', ' RSSSSRRRSSSSR ',
    ' SSSSSSSSSSSSS ',
  ], { R: '#5c2438', A: '#ff87b3', W: '#f0e6ff', C: '#160a12' }),

  // --- Challenge-exclusive bosses (data/challengeBosses.js) ---
  // Note the in-game body color is still the shared crimson→black damage
  // ramp (Game._drawBoss's paletteOverride.R always wins), so identity
  // rides on the accent letters: teal foam, green iris, indigo frame.

  // A curling wave rolled into a spiral — the maw of the whirlpool.
  maelstromVerge: bossSprite([
    '      AAAA     ', '    AAWWWAA    ', '   AAWRRRWA    ', '  AWRRRRRRWA   ',
    '  AWRA  ARRA   ', ' AWRA    ARRA  ', ' AWR      RRA  ', ' AWRA      RA  ',
    '  AWRA    RRA  ', '   AWRRARRRA   ', '    AWRRRRA    ', '   ARRRRRA     ',
    '  ARRRRRA      ', '  ARRRRRRAA    ', '  SRRRRRRRRA   ', ' SSSRRRRRRSS   ',
    '  SSSSSSSSS    ',
  ], { R: '#1a5f66', A: '#4dd8c7', W: '#ffffff' }),

  // A lidless eye set in a fleshy idol — sclera, green iris, pinprick glint.
  theUnblinking: bossSprite([
    '     RRRRR     ', '   RRRRRRRRR   ', '  RRRRRRRRRRR  ', ' RRRWWWWWWWRR  ',
    ' RRWWWAAAWWWRR ', 'RRWWAAAAAAAWWRR', 'RRWAAACCCAAAWRR', 'RRWAACCCCCAAWRR',
    'RRWAACCWCCAAWRR', 'RRWAAACCCAAAWRR', ' RRWWAAAAAWWRR ', ' RRRWWWWWWWRRR ',
    '  RRRRRRRRRRR  ', '   RRRRRRRRR   ', '    RRSSSRR    ', '   RR SSS RR   ',
    '  R    S    R  ',
  ], { R: '#2c4a24', A: '#8ae06e', W: '#e8f5e0', C: '#0c130a' }),

  // An hourglass with a thread of falling sand, standing on claw feet.
  chronophage: bossSprite([
    ' AAAAAAAAAAAAA ', ' ARRRRRRRRRRRA ', '  ARRRRRRRRRA  ', '   ARRRWRRRA   ',
    '    ARRWRRA    ', '     ARWRA     ', '      AWA      ', '      ARA      ',
    '      AWA      ', '     ARWRA     ', '    ARRWRRA    ', '   ARRRWRRRA   ',
    '  ARRRRRRRRRA  ', ' ARRRRRRRRRRRA ', ' AAAAAAAAAAAAA ', '   SS     SS   ',
    '  SS       SS  ',
  ], { R: '#2f2a52', A: '#8c7bff', W: '#f0ecff' }),

  sirenreef: bossSprite([
    '      AAA      ', '   A RRR A    ', '  ARRRRRRRA   ', ' ARRRWWRRRRA  ',
    'ARRRCCCCRRRRA', 'RRRCCCCCCCCRRR', 'RRCCCCCCCCCCRR', 'RRCCCCCCCCCCRR',
    ' RRRCCCCCRRR  ', '  RRRRRRRRRR   ', '   RRRRRRRR    ', '    RRSSRR     ',
    '   SSSSSSSS    ', '  SS      SS   ',
  ], { R: '#46304b', A: '#ff6b9d', W: '#fff0f8', C: '#101827' }),

  palimpsest: bossSprite([
    '   AAAAAAA     ', '  ARR R RRA    ', ' ARRRRRRRRRA   ', ' ARRW RWRRA   ',
    '  ARRRCRRRA    ', '  RRRCCCCRR    ', ' RRRRCCCCRRRR  ', ' RRRRRRRRRRRR  ',
    '  RRRRRRRRRR   ', '   RRSSSSRR    ', '  SSSSSSSSSS   ', ' SS   SS   SS  ',
  ], { R: '#49375c', A: '#d58cff', W: '#f6eaff', C: '#100b1e' }),

  // --- Act II: The Undersong (data/act2Bosses.js) ---
  // A cowled shroud opening onto pure dark, with one faint cold-blue mote low
  // in the hood — the last light in a lightless deep. Body on R per the
  // shared ramp; C is the hood's void, A the mote, W its glint.
  gloam: bossSprite([
    '     RRRRR     ', '   RRRRRRRRR   ', '  RRRRRRRRRRR  ', ' RRRCCCCCCCRRR ',
    ' RRCCCCCCCCCRR ', 'RRCCCCCCCCCCCRR', 'RRCCCCCCCCCCCRR', 'RRCCCCCCCCCCCRR',
    'RRCCCCCCCCCCCRR', 'RRCCCCCAACCCCRR', 'RRCCCCAWWACCCRR', 'RRCCCCCAACCCCRR',
    'RRRCCCCCCCCCRRR', ' RRRCCCCCCCRRR ', '  RRRRCCCRRRR  ', '   RRSSSSSRR   ',
    '  SS  S S  SS  ',
  ], { R: '#22304a', A: '#6ea8ff', W: '#eaf2ff', C: '#05060a' }),

  // Two half-figures leaning toward each other across a gap, singing — warm
  // voice (A) on the left, cold voice (B) on the right, a body split down the
  // middle. The two tones are the fight (polarity); the silhouette says so.
  antiphon: bossSprite([
    '   AAA   BBB   ', '  AARRR RRRBB  ', '  ARRRR RRRRB  ', '  ARWRR RRWRB  ',
    '  ARRRR RRRRB  ', '  AARRR RRRBB  ', '   ARRR RRRB   ', '    RRR RRR    ',
    '    RRRRRRR    ', '   RRRRRRRRR   ', '   RRRRRRRRR   ', '   ARRR RRRB   ',
    '  AARRR RRRBB  ', '  ARRRR RRRRB  ', '   ARR   RRB   ', '   SSS   SSS   ',
    '  SS       SS  ',
  ], { R: '#3a2a4a', A: '#ff8a4d', B: '#4dc9ff', W: '#ffffff' }),

  // A smooth, faceless head — closed-eye dashes and a long sealed mouth-bar,
  // all in the same drained grey the color goes where sound dies. It fights
  // with less, and it looks it: nothing here but silence.
  greatHush: bossSprite([
    '    RRRRRRR    ', '   RRRRRRRRR   ', '  RRRRRRRRRRR  ', '  RRRRRRRRRRR  ',
    '  RRAARRAARRR  ', '  RRRRRRRRRRR  ', '  RRRRRRRRRRR  ', '  RRAAAAAAARR  ',
    '  RRRRRRRRRRR  ', '  RRRRRRRRRRR  ', '   RRRRRRRRR   ', '   RRRRRRRRR   ',
    '    RRRRRRR    ', '    RRRRRRR    ', '    RRSSSRR    ', '   RSS S SSR   ',
    '  SS   S   SS  ',
  ], { R: '#2c3030', A: '#9fb0a8', S: '#171a1a' }),

  // A heavy round mass with a bright collapsing core — a place where down got
  // heavier than it should. The accent ring (A) marks the pull; the white core
  // (W/C) is the weight everything falls toward.
  ballast: bossSprite([
    '     RRRRR     ', '   RRRRRRRRR   ', '  RRRRRRRRRRR  ', ' RRRRAAAAARRRR ',
    ' RRRAWWWWWARRR ', 'RRRAWWWWWWWARRR', 'RRRAWWWCWWWARRR', 'RRRAWWWWWWWARRR',
    'RRRAWWWWWWWARRR', ' RRRAWWWWWARRR ', ' RRRRAAAAARRRR ', '  RRRRRRRRRRR  ',
    '   RRRRRRRRR   ', '    RRRRRRR    ', '    RSSSR      ', '   SS   SS     ',
  ], { R: '#2a2440', A: '#b98cff', W: '#e8dcff', C: '#ffffff' }),

  // A pulsing core ringed by radiating wave-dots (A) — the swell rising to
  // meet you. Bright center (W/C) that reads as building pressure.
  swell: bossSprite([
    '  A  RRRRR  A  ', '   ARRRRRRR A  ', '  ARRRRRRRRRA  ', ' A RRRRRRRRR A ',
    '  ARRRWWWRRRA  ', ' A RRWWWWWRR A ', '  ARRWWCWWRRA  ', ' A RRWWWWWRR A ',
    '  ARRRWWWRRRA  ', ' A RRRRRRRRR A ', '  ARRRRRRRRRA  ', '   ARRRRRRR A  ',
    '  A  RRRRR  A  ', '    RR   RR    ', '   SS     SS   ',
  ], { R: '#12303a', A: '#57e0d0', W: '#a8f0e6', C: '#ffffff' }),

  // No face because it had no maker — a barely-there dark mass with a cold
  // white heart, dissolving into scattered motes at its edges. The deep given
  // just enough shape to move.
  undersong: bossSprite([
    '     WRRRW     ', '   RRRRRRRRR   ', '  RRRCCCCCRRR  ', ' RRCCCCCCCCCRR ',
    ' RCCCCCWCCCCCR ', 'RRCCCCWWWCCCCRR', 'RRCCCCCWCCCCCRR', 'RRCCCCCCCCCCCRR',
    ' RCCCCCCCCCCCR ', ' RRCCCCCCCCCRR ', '  RRRCCCCCRRR  ', '   RRRRRRRRR   ',
    '  W RRRRRRR W  ', '   R  W W  R   ', '  W  R   R  W  ',
  ], { R: '#100d18', C: '#050507', W: '#e0e6ff' }),
  undersongPhase2: bossSprite([
    '    WWWWW     ', '  RRWWWWW R   ', ' RRRCCCCCRRR  ', 'RRCCCCCCCCCRR ',
    'RCCCCWWCCCCCR', 'CCCCWWWWWWCCC', 'CCCCWWWWWWCCC', 'RCCCCWWCCCCCR ',
    ' RRRCCCCCRRR  ', '  RR RRR RR    ', ' W  R   R  W  ', '   SSSSSSS     ',
  ], { R: '#241833', C: '#020205', W: '#ffffff' }),

  // --- Act III: The Anthem (data/act3Bosses.js) ----------------------------
  // Every silhouette in this act is built out of the player: a repeated figure,
  // a copy, a scale, a doubling, an unfinished line. The act's principle is
  // authorship, and the sprites are the first place that has to read.

  // A figure repeated down and to the right in fading echoes (A, then B) —
  // the same shape, a breath late, which is exactly what the fight is.
  ostinato: bossSprite([
    '   RRRRR       ', '  RRRRRRR      ', '  RRWRWRR      ', '  RRRRRRR      ',
    '  RRRRRRR      ', '   RRRRR AAAAA ', '   RRRRR AAAAA ', '  RRRRRRRAAAAA ',
    '  RRRRRRR AAA  ', '   RRRRR   BBB ', '   RRRRR   BBB ', '   RRRRR   BBB ',
    '   SSSSS       ', '  SS   SS      ', ' SS     SS     ',
  ], { R: '#3a3348', A: '#ffd166', B: '#6b5a2e', W: '#ffffff' }),

  // A blank understudy's mask with a player-shaped hollow at its centre — no
  // kit of its own, only the space where yours goes.
  understudy: bossSprite([
    '    RRRRRRR    ', '   RRRRRRRRR   ', '  RRRRRRRRRRR  ', '  RRAARRRAARR  ',
    '  RRRRRRRRRRR  ', '  RRRAAAAARRR  ', '  RRAWWWWWARR  ', '  RRAWAAAWARR  ',
    '  RRAWAWAWARR  ', '  RRAWAAAWARR  ', '  RRAWWWWWARR  ', '  RRRAAAAARRR  ',
    '   RRRRRRRRR   ', '    RRRRRRR    ', '    RSSSSSR    ', '   SS     SS   ',
  ], { R: '#1c2a24', A: '#7fe0a0', W: '#0c0e14' }),

  // A scale-pan and a coin: the collector, and the only part of it you ever
  // really deal with. Warm gold on cold ash.
  tithe: bossSprite([
    '       A       ', '   AAAAAAAAA   ', '   A   R   A   ', '  AAA RRR AAA  ',
    '  AWA RRRRRAWA ', '  AAA RRRRR AAA', '   A  RRWRR  A ', '      RRRRR    ',
    '     RRRRRRR   ', '    RRRRRRRRR  ', '   RRRRRRRRRRR ', '   RRRAAARRRRR ',
    '   RRRAWARRRRR ', '   RRRAAARRRRR ', '    RRRRRRRRR  ', '     SSSSSSS   ',
  ], { R: '#2c2418', A: '#d9a441', W: '#fff0c8' }),

  // Two identical half-bodies folded across a mirror line, with the line drawn
  // — one shape, obeying one hand, in two places.
  manifold: bossSprite([
    '  RRRR W RRRR  ', ' RRRRR W RRRRR ', ' RRARR W RRARR ', ' RRRRR W RRRRR ',
    '  RRRR W RRRR  ', '   RRR W RRR   ', '  RRRRRWRRRRR  ', ' RRRRRRWRRRRRR ',
    '  RRRRRWRRRRR  ', '   RRR W RRR   ', '  RRRR W RRRR  ', ' RRARR W RRARR ',
    ' RRRRR W RRRRR ', '  RRRR W RRRR  ', '  SSS  W  SSS  ',
  ], { R: '#16323a', A: '#66d9ff', W: '#bff0ff' }),

  // An unfinished stanza: three ruled lines, the last one trailing off, and a
  // pen-nib hanging over it waiting for you.
  verse: bossSprite([
    '      AAA      ', '     AAAAA     ', '      AWA      ', '       A       ',
    '  RRRRRRRRRRR  ', '  RRRRRRRRRRR  ', '               ', '  RRRRRRRRRRR  ',
    '  RRRRRRRRRRR  ', '               ', '  RRRRRRR      ', '  RRRRR        ',
    '  RRR          ', '  R            ', '  SS           ',
  ], { R: '#3a2a44', A: '#ff8ac4', W: '#ffffff' }),

  // The finale, and the one bright thing in the game: a great open chord of
  // rising voices with a small dark figure at its base — you, at the bottom of
  // something enormous that is no longer yours. Body ink-on-daylight, inverted
  // from every other boss in the roster.
  anthem: bossSprite([
    ' A A A A A A A ', '  AAAAAAAAAAA  ', '  ARRRRRRRRRA  ', '  RRRRRRRRRRR  ',
    ' RRRRRRRRRRRRR ', ' RRRWRRRRRWRRR ', ' RRRRRRRRRRRRR ', ' RRRRRWWWRRRRR ',
    ' RRRRWWWWWRRRR ', ' RRRRRWWWRRRRR ', '  RRRRRRRRRRR  ', '  RRRRRRRRRRR  ',
    '   RRRRRRRRR   ', '     RRRRR     ', '      RRR      ', '      SSS      ',
  ], { R: '#1a1620', A: '#f2f0ea', W: '#ffd166', S: '#5a5348' }),
  anthemPhase2: bossSprite([
    'AAAAAAAAAAAAAAA', 'AARRRRRRRRRRRAA', 'ARRRRRRRRRRRRRA', 'RRRRWRRRRRWRRRR',
    'RRRRRRRRRRRRRRR', 'RRRWWWWWWWWWRRR', 'RRWWWWWWWWWWWRR', 'RRRWWWWWWWWWRRR',
    'RRRRRRRRRRRRRRR', 'ARRRRRRRRRRRRRA', 'AARRRRRRRRRRRAA', ' AAAA RRR AAAA ',
    '      RRR      ', '      SSS      ',
  ], { R: '#0d0a12', A: '#ffffff', W: '#ffd166', S: '#5a5348' }),
};

// Weapon icons — tiny dots tinted to match each weapon's own projectile
// color (WEAPONS in config/constants.js), keyed by display name since
// that's all Game.js's _drawWeaponIcons has on hand (combat.weapons
// entries only expose `.name`, not their config id).
function buildWeaponIcon(color) {
  return defineSprite({ pixelSize: 1, palette: { R: color }, rows: buildSilhouette(2, { seed: 3 }) });
}

// Minions — small enough to read instantly as "not the boss" next to it, one
// distinct silhouette/color per type (see entities/Skirmisher|Charger|Bomber.js)
// so the player can tell at a glance which behavior is closing in.
export const MINION_SPRITES = {
  skirmisher: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 5, jitter: 0.3, seed: 44 }), '#c04ae0');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  // Fewer, sharper spikes than the Skirmisher — reads as more angular/
  // arrow-like, matching its charge-in-a-straight-line behavior.
  charger: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 3, jitter: 0.45, seed: 52 }), '#ff8a4a');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  // A single bright "fuse spark" accent so it visually reads as volatile —
  // about to detonate — rather than just another blob.
  bomber: (() => {
    const rows = buildSilhouette(detailedRadius(4, DETAIL), { spikes: 6, jitter: 0.2, seed: 63 });
    const center = Math.floor(rows.length / 2);
    const withAccent = stampAccent(rows, center, center, DETAIL, 'A');
    const { rows: finished, palette } = finishCreature(withAccent, '#8a1a24', { A: '#ffe27a' });
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows: finished });
  })(),
  // Few spikes, low jitter — reads as blocky/armored rather than feral,
  // matching its defensive identity. The shield itself is a runtime tint
  // (see Game.js's _drawMinions paletteOverride), not baked into the sprite,
  // so it still reads as a Warden even once its link is dead and it's
  // vulnerable like everything else.
  warden: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 2, jitter: 0.15, seed: 70 }), '#4a6fa5');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  // A bright "casting core" accent, same visual trick as Bomber's fuse
  // spark — reinforces that this one channels something before it hurts you.
  caster: (() => {
    const rows = buildSilhouette(detailedRadius(4, DETAIL), { spikes: 4, jitter: 0.35, seed: 71 });
    const center = Math.floor(rows.length / 2);
    const withAccent = stampAccent(rows, center, center, DETAIL, 'A');
    const { rows: finished, palette } = finishCreature(withAccent, '#7a4ae0', { A: '#e0c8ff' });
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows: finished });
  })(),
  // Same "volatile" accent language as Bomber — this one also detonates,
  // just repeatably instead of on death.
  trapper: (() => {
    const rows = buildSilhouette(detailedRadius(4, DETAIL), { spikes: 5, jitter: 0.25, seed: 72 });
    const center = Math.floor(rows.length / 2);
    const withAccent = stampAccent(rows, center, center, DETAIL, 'A');
    const { rows: finished, palette } = finishCreature(withAccent, '#c08a3a', { A: '#ffe27a' });
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows: finished });
  })(),
  splitter: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 6, jitter: 0.3, seed: 73 }), '#e04a8a');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  leech: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 3, jitter: 0.4, seed: 74 }), '#4ae08a');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  swarmling: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 5, jitter: 0.5, seed: 75 }), '#e0d54a');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  orbiter: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 4, jitter: 0.2, seed: 76 }), '#4ae0c0');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),

  // --- Act II: the deep (ACT2-BIBLE.md §8) ---------------------------------
  // Ragged and long-spiked — all silhouette, since most of the time you see
  // its lure and not this at all (Game._drawMinions). Cold blue matches the
  // Gloam's accent, the boss it foreshadows.
  anglerling: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 6, jitter: 0.5, seed: 91 }), '#6ea8ff');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  // Base color is deliberately neutral: the body is recolored to its tone at
  // draw time (Game._drawMinions), because which tone it carries is the only
  // thing about it the player actually needs to read.
  mirrorShade: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 3, jitter: 0.35, seed: 96 }), '#9a8fc0');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  // Round, smooth, and drained of color — the Great Hush's palette. Nothing
  // about it reads as aggressive, which is the point: it doesn't hurt you.
  muffler: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 1, jitter: 0.12, seed: 103 }), '#9fb0a8');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  // Bigger and blunt — mass, not menace. Violet, matching the Ballast.
  sinkweight: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(5, DETAIL), { spikes: 2, jitter: 0.15, seed: 109 }), '#b98cff');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  // The Famished Court's supplicants (see patterns/TheFeast.js) — bile-
  // tinted and nearly round: the one minion that ignores the player and
  // shuffles toward the boss's maw instead.
  courtier: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 2, jitter: 0.2, seed: 81 }), '#a8b03a');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),

  // --- Act III: the new song (ACT3-BIBLE.md §8) ----------------------------
  // Each echoes a boss texture, so a wave stage teaches the rule before the
  // boss demands it — and each is coloured to the boss it foreshadows.

  // Smooth and featureless: it isn't hunting you, it's re-walking you.
  tracer: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 1, jitter: 0.1, seed: 131 }), '#ffd166');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  // Small, round, imitative — a weak copy of you, and it looks like one.
  mimicMote: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(3, DETAIL), { spikes: 2, jitter: 0.2, seed: 137 }), '#7fe0a0');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  // Heavy and boxy — a strongbox with legs. It doesn't hurt you, it charges you.
  tollkeeper: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(5, DETAIL), { spikes: 1, jitter: 0.08, seed: 149 }), '#d9a441');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
  // Paired and spiky, in the Manifold's cold blue: never one of them.
  twinbound: (() => {
    const { rows, palette } = finishCreature(buildSilhouette(detailedRadius(4, DETAIL), { spikes: 4, jitter: 0.3, seed: 151 }), '#66d9ff');
    return defineSprite({ pixelSize: 1 / DETAIL, palette, rows });
  })(),
};

// Health pod — a small green cross on a dark tile, reusing the HUD's
// "healthy" green (#4ade80) so it reads as a heal pickup at a glance.
export const HEALTH_POD_SPRITE = defineSprite({
  pixelSize: 4, // 4x bigger (was 1) — more visible/readable as a pickup among the bullet-hell clutter
  palette: { R: '#2a2a3a', W: '#4ade80' },
  rows: [
    'RRRRR',
    'RRWRR',
    'RWWWR',
    'RRWRR',
    'RRRRR',
  ],
});

/**
 * The Spoils chest as a physical drop (BULLET-HEAVEN.md §H3) — the thing a
 * boss leaves on the floor, walked over to open the reel.
 *
 * Sized to sit alongside HEALTH_POD_SPRITE (20px across) rather than to the
 * player's 4px radius: like the pod, it is a pickup that has to be findable
 * across an arena still full of bullets, not a creature to be judged at scale.
 *
 * The trim ('M') is the only tier-carrying color — SPOILS_CHEST_TRIM feeds it
 * to drawSprite as a paletteOverride, so plain/gilded/radiant is one palette
 * substitution rather than three near-identical sprite definitions.
 */
export const SPOILS_CHEST_SPRITE = defineSprite({
  pixelSize: 2,
  palette: {
    D: '#140f1c', // outline — the same near-black every creature sprite is bordered with
    L: '#9a6a33', // lid highlight (top-down light)
    B: '#6b4423', // body
    S: '#432a15', // underside shade
    M: '#f4e04d', // trim + lock plate; overridden per tier
    K: '#241708', // keyhole
  },
  rows: [
    '  DDDDDDDD  ',
    ' DLLLLLLLLD ',
    'DLLLLLLLLLLD',
    'DBBBBBBBBBBD',
    'DMMMMMMMMMMD',
    'DBBBBMMBBBBD',
    'DBBBBKKBBBBD',
    'DBBBBBBBBBBD',
    'DSSSSSSSSSSD',
    ' DDDDDDDDDD ',
  ],
});

/** Trim color per chest tier — matches the overlay title's own tier colors (style.css). */
export const SPOILS_CHEST_TRIM = {
  plain: '#c8ccda',
  gilded: '#f4e04d', // --gold
  radiant: '#c9a8ff', // --arcane
};

export const WEAPON_SPRITES = {
  [WEAPONS.spiritLance.name]: buildWeaponIcon(WEAPONS.spiritLance.color),
  [WEAPONS.scatterCannon.name]: buildWeaponIcon(WEAPONS.scatterCannon.color),
  [WEAPONS.arcNeedle.name]: buildWeaponIcon(WEAPONS.arcNeedle.color),
  [WEAPONS.orbitWard.name]: buildWeaponIcon(WEAPONS.orbitWard.color),
  [WEAPONS.novaPulse.name]: buildWeaponIcon(WEAPONS.novaPulse.color),
};

// Recolors filled ('R') cells from `fromFraction` down to the bottom of the
// grid to `shadeChar` — a soft "ground contact" shadow along a round
// creature's underside, the same top-down-light cue every hand-drawn
// creature sprite uses for volume. Mirror of capBandRange above (which
// bands from the top), reused here for the opposite edge.
function shadeBelow(rows, fromFraction, shadeChar) {
  const grid = rows.map((r) => r.split(''));
  const fromRow = Math.round(grid.length * fromFraction);
  for (let y = fromRow; y < grid.length; y++) {
    for (let x = 0; x < grid[y].length; x++) {
      if (grid[y][x] === 'R') grid[y][x] = shadeChar;
    }
  }
  return grid.map((r) => r.join(''));
}

// Places a pair of 2x2 glowing eye blocks (plus a 1px white highlight speck
// in each, the classic "glossy eye" pixel-art cue) at a given row fraction —
// bigger and more expressive than a single flat pixel.
function addBigEyes(rows, eyeChar, highlightChar, rowFraction, spacing) {
  const grid = rows.map((r) => r.split(''));
  const row = Math.max(0, Math.min(grid.length - 2, Math.round(grid.length * rowFraction)));
  const centerCol = Math.floor(grid[0].length / 2);
  for (const side of [-1, 1]) {
    const cx = centerCol + side * spacing;
    for (const dy of [0, 1]) {
      for (const dx of [0, 1]) {
        if (grid[row + dy]?.[cx + dx] !== undefined) grid[row + dy][cx + dx] = eyeChar;
      }
    }
    if (grid[row]?.[cx] !== undefined) grid[row][cx] = highlightChar;
  }
  return grid.map((r) => r.join(''));
}

// One creature per pet: a round buildSilhouette body plus a small merged
// "feature" piece (ears/tuft/fin/horns/tail) that gives each a distinct
// silhouette instead of 5 identically-shaped blobs in different colors —
// same mergeSilhouettes technique as the boss idols' flanking shards/crown
// above. Then: bottom shading (shadeBelow) for volume, big glossy eyes
// (addBigEyes), and a dark outline (Sprite.js's outlineRows) last, since
// that has to wrap the fully-assembled silhouette. PLACEHOLDER ART: these
// are stand-ins for real per-pet artwork, not a permanent design — swap a
// pet's entry here for a real asset-backed sprite before anything using
// these ships publicly. Kept in this procedural style purely so the shop
// gallery has something to render today.
function buildPetSprite(bodyColor, accentColor, seed, featurePieces = []) {
  const body = buildSilhouette(6, { spikes: 5, jitter: 0.3, seed });

  let rows = body;
  if (featurePieces.length > 0) {
    const bodyW = body[0].length;
    const bodyH = body.length;
    const halfNeeded = featurePieces.reduce(
      (m, f) => Math.max(m, Math.abs(f.dx) + f.rows[0].length / 2, Math.abs(f.dy) + f.rows.length / 2),
      Math.max(bodyW, bodyH) / 2
    );
    const size = Math.ceil(halfNeeded) * 2 + 2;
    const center = Math.floor(size / 2);
    const pieces = [
      { rows: body, offsetX: center - Math.floor(bodyW / 2), offsetY: center - Math.floor(bodyH / 2) },
      ...featurePieces.map(({ rows: r, dx, dy }) => ({
        rows: r,
        offsetX: center - Math.floor(r[0].length / 2) + dx,
        offsetY: center - Math.floor(r.length / 2) + dy,
      })),
    ];
    rows = mergeSilhouettes(size, pieces);
  }

  rows = shadeBelow(rows, 0.6, 'S');
  rows = addBigEyes(rows, 'A', 'H', 0.42, 3);
  rows = outlineRows(rows, 'O');

  const palette = { R: bodyColor, S: darkenHex(bodyColor, 0.35), A: accentColor, H: '#ffffff', O: '#0a0a12' };
  return defineSprite({ pixelSize: 3, palette, rows });
}

// Standard feature-piece loadouts that scale with a pet's rarity tier
// (data/pets.js) so rarity reads directly off the silhouette: commons are
// plain blobs, uncommons grow a single tuft, rares get flanking ears/fins,
// epics get tall horns, and legendaries wear a three-spike crown. The seed
// still varies each pet's exact blob shapes, so two pets of the same tier
// don't share a silhouette.
function rarityFeatures(rarity, seed) {
  switch (rarity) {
    case 'uncommon':
      return [{ rows: buildSilhouette(2, { spikes: 4, jitter: 0.4, seed: seed + 1 }), dx: 0, dy: -7 }];
    case 'rare':
      return [
        { rows: buildSilhouette(2, { spikes: 3, jitter: 0.35, seed: seed + 1 }), dx: -5, dy: -5 },
        { rows: buildSilhouette(2, { spikes: 3, jitter: 0.35, seed: seed + 2 }), dx: 5, dy: -5 },
      ];
    case 'epic':
      return [
        { rows: buildSilhouette(3, { spikes: 2, jitter: 0.2, seed: seed + 1 }), dx: -3, dy: -8 },
        { rows: buildSilhouette(3, { spikes: 2, jitter: 0.2, seed: seed + 2 }), dx: 3, dy: -8 },
      ];
    case 'legendary':
      return [
        { rows: buildSilhouette(2, { spikes: 3, jitter: 0.25, seed: seed + 1 }), dx: -5, dy: -7 },
        { rows: buildSilhouette(3, { spikes: 3, jitter: 0.25, seed: seed + 2 }), dx: 0, dy: -9 },
        { rows: buildSilhouette(2, { spikes: 3, jitter: 0.25, seed: seed + 3 }), dx: 5, dy: -7 },
      ];
    case 'mythical':
      return [
        { rows: buildSilhouette(2, { spikes: 4, jitter: 0.15, seed: seed + 1 }), dx: -7, dy: -7 },
        { rows: buildSilhouette(2, { spikes: 4, jitter: 0.15, seed: seed + 2 }), dx: 7, dy: -7 },
        { rows: buildSilhouette(2, { spikes: 5, jitter: 0.1, seed: seed + 3 }), dx: 0, dy: -12 },
      ];
    case 'rainbow':
      return [
        { rows: buildSilhouette(2, { spikes: 5, jitter: 0.1, seed: seed + 1 }), dx: -8, dy: 0 },
        { rows: buildSilhouette(2, { spikes: 5, jitter: 0.1, seed: seed + 2 }), dx: 8, dy: 0 },
        { rows: buildSilhouette(2, { spikes: 4, jitter: 0.1, seed: seed + 3 }), dx: 0, dy: 10 },
      ];
    default: // common — a plain, featureless blob.
      return [];
  }
}

export const PET_SPRITES = {
  // Legacy keys retained for save compatibility; their silhouettes now use
  // the same rarity language as every newer themed pet.
  ember:  buildPetSprite('#4f2d87', '#c8b4ff', 91, rarityFeatures('common', 91)),
  mossy:  buildPetSprite('#78d86b', '#d6ff8a', 92, rarityFeatures('common', 92)),
  glimmer: buildPetSprite('#f0b23c', '#fff3a3', 93, rarityFeatures('legendary', 93)),
  shade:   buildPetSprite('#241b3f', '#e05cff', 94, rarityFeatures('epic', 94)),
  frost:   buildPetSprite('#4eb4e8', '#baf5ff', 95, rarityFeatures('rare', 95)),

  // Fire set — smolder-red commons up to a white-hot legendary sun spirit.
  cinder:  buildPetSprite('#b8563a', '#e8a05a', 110, rarityFeatures('common', 110)),
  scorch:  buildPetSprite('#d86a3a', '#ffd27a', 112, rarityFeatures('uncommon', 112)),
  blaze:   buildPetSprite('#e8542a', '#ffb84d', 114, rarityFeatures('rare', 114)),
  pyre:    buildPetSprite('#c22e2e', '#ff7a3a', 116, rarityFeatures('epic', 116)),
  solaris: buildPetSprite('#ff9a2e', '#fff2b8', 118, rarityFeatures('legendary', 118)),

  // Water set — soft shallows blues down to a deep-sea legendary serpent.
  droplet:   buildPetSprite('#7ab8d8', '#d8f0ff', 120, rarityFeatures('common', 120)),
  ripple:    buildPetSprite('#5aa8e0', '#b8e2ff', 122, rarityFeatures('uncommon', 122)),
  tide:      buildPetSprite('#3a86d8', '#8ac8ff', 124, rarityFeatures('rare', 124)),
  abyss:     buildPetSprite('#2a4a9a', '#7a9aff', 126, rarityFeatures('epic', 126)),
  leviathan: buildPetSprite('#1a6ab8', '#aef2ff', 128, rarityFeatures('legendary', 128)),

  // Earth set — dusty browns and mosses up to a gilded legendary colossus.
  pebble:  buildPetSprite('#9a8a6a', '#c8b894', 130, rarityFeatures('common', 130)),
  burrow:  buildPetSprite('#a8763a', '#e0b87a', 132, rarityFeatures('uncommon', 132)),
  boulder: buildPetSprite('#7a6a4a', '#b8a878', 134, rarityFeatures('rare', 134)),
  terra:   buildPetSprite('#5a8a3a', '#a8d87a', 136, rarityFeatures('epic', 136)),
  titan:   buildPetSprite('#8a7a3a', '#ffe08a', 138, rarityFeatures('legendary', 138)),

  // Wind set — pale sky tones up to a storm-violet legendary vortex.
  breeze:    buildPetSprite('#a8c8b8', '#e8f8f0', 140, rarityFeatures('common', 140)),
  gust:      buildPetSprite('#8ab8a8', '#d8f0e0', 142, rarityFeatures('uncommon', 142)),
  zephyr:    buildPetSprite('#6ab8c8', '#c8f0f8', 144, rarityFeatures('rare', 144)),
  tempest:   buildPetSprite('#5a7ab8', '#b8d0ff', 146, rarityFeatures('epic', 146)),
  maelstrom: buildPetSprite('#7a5ac8', '#d8c8ff', 148, rarityFeatures('legendary', 148)),

  // Light set — warm golds and pearl-white highlights.
  lumen:   buildPetSprite('#f6e38a', '#ffffff', 250, rarityFeatures('common', 250)),
  halo:    buildPetSprite('#f7d36a', '#fff7cf', 252, rarityFeatures('uncommon', 252)),
  beacon:  buildPetSprite('#ffe47e', '#fffef0', 254, rarityFeatures('rare', 254)),
  sol:     buildPetSprite('#f0b23c', '#fff3a3', 256, rarityFeatures('epic', 256)),
  // Dark set — shadowed violet bodies with saturated neon cores.
  gloom:   buildPetSprite('#25213f', '#7f6cff', 260, rarityFeatures('common', 260)),
  murk:    buildPetSprite('#312345', '#b36cff', 262, rarityFeatures('uncommon', 262)),
  umbra:   buildPetSprite('#241b3f', '#e05cff', 264, rarityFeatures('rare', 264)),
  eclipse: buildPetSprite('#151225', '#ff6fae', 266, rarityFeatures('legendary', 266)),
  // Frost set — icy blue bodies and cyan crystalline accents.
  snowcap: buildPetSprite('#b9eaff', '#ffffff', 270, rarityFeatures('common', 270)),
  rime:    buildPetSprite('#8ed9ff', '#e9fbff', 272, rarityFeatures('uncommon', 272)),
  glacier: buildPetSprite('#4eb4e8', '#baf5ff', 274, rarityFeatures('epic', 274)),
  whiteout: buildPetSprite('#d7f8ff', '#a8ffff', 276, rarityFeatures('legendary', 276)),
  // Moss set — living greens with earthy bark and amber accents.
  sprout:  buildPetSprite('#78d86b', '#d6ff8a', 280, rarityFeatures('uncommon', 280)),
  grove:   buildPetSprite('#3fa85f', '#b8f28a', 282, rarityFeatures('rare', 282)),
  bramble: buildPetSprite('#32754a', '#d39b67', 284, rarityFeatures('epic', 284)),
  ancient: buildPetSprite('#526b3b', '#e5c86e', 286, rarityFeatures('legendary', 286)),
  // Arcane set — indigo spellcraft with pink, cyan, and gold glyph light.
  glyph:   buildPetSprite('#5143a6', '#c8b4ff', 290, rarityFeatures('uncommon', 290)),
  sigil:   buildPetSprite('#40318e', '#ff9ce8', 292, rarityFeatures('rare', 292)),
  aether:  buildPetSprite('#273e91', '#8df4ff', 294, rarityFeatures('epic', 294)),
  arcana:  buildPetSprite('#4f2d87', '#ffd477', 296, rarityFeatures('legendary', 296)),

  // Mythical pets — astral bodies with violet/magenta cores and a three-point
  // crown silhouette, visually distinct from the elemental/legendary sets.
  mythicMoss:  buildPetSprite('#6539b8', '#ff9ef5', 210, rarityFeatures('mythical', 210)),
  mythicFlare: buildPetSprite('#a83c78', '#ffd36e', 212, rarityFeatures('mythical', 212)),
  mythicDepth: buildPetSprite('#24498f', '#82e8ff', 214, rarityFeatures('mythical', 214)),
  mythicCore:  buildPetSprite('#57428f', '#e7c3ff', 216, rarityFeatures('mythical', 216)),
  mythicStorm: buildPetSprite('#393d9b', '#b5a8ff', 218, rarityFeatures('mythical', 218)),

  // Rainbow pets — prismatic bodies with bright tri-color accents and a
  // wider halo-like silhouette.
  rainbowAurora:   buildPetSprite('#e85fa8', '#7de8ff', 230, rarityFeatures('rainbow', 230)),
  rainbowInferno:  buildPetSprite('#f06d3d', '#fff06e', 232, rarityFeatures('rainbow', 232)),
  rainbowTide:     buildPetSprite('#3d9ed1', '#ff9ee8', 234, rarityFeatures('rainbow', 234)),
  rainbowMonolith: buildPetSprite('#7e6bc2', '#a9ff92', 236, rarityFeatures('rainbow', 236)),
  rainbowTempest:  buildPetSprite('#5e81d1', '#ffd88a', 238, rarityFeatures('rainbow', 238)),
};

// Cosmetic hats (data/cosmetics.js) — small overlay sprites drawn above the
// player's head in Game._drawPlayer, on top of the body, purely vanity.
// Hand-drawn pixel grids (space = transparent); each row within a hat is the
// same width. pixelSize is a touch larger than the player body's 1/DETAIL so
// a hat reads clearly perched on the ~9px-wide head silhouette.
export const HAT_SPRITES = {
  crown: defineSprite({
    pixelSize: 0.6,
    palette: { G: '#f4e04d', J: '#ff5d6c', D: '#b89b1e' },
    rows: [
      'G G G G G',
      'GGGGGGGGG',
      'GGGJGGGJG',
      'DDDDDDDDD',
    ],
  }),
  tophat: defineSprite({
    pixelSize: 0.6,
    palette: { T: '#45455f', G: '#f4e04d' },
    rows: [
      ' TTTTT ',
      ' TTTTT ',
      ' TTTTT ',
      ' GGGGG ',
      'TTTTTTT',
    ],
  }),
  cap: defineSprite({
    pixelSize: 0.6,
    palette: { C: '#4dd8c7', B: '#2e8a7f' },
    rows: [
      '  CCCCC  ',
      ' CCCCCCC ',
      ' CCCCCCC ',
      '   BBBBBB',
    ],
  }),
  halo: defineSprite({
    pixelSize: 0.6,
    palette: { H: '#ffe98a' },
    rows: [
      ' HHHHHHH ',
      'HH     HH',
      ' HHHHHHH ',
    ],
  }),
};
